/**
 * Integer rounding of an exact rational `numerator / denominator`.
 *
 * Every money operation that is not exact — a percentage, an exchange rate, a
 * prorated share — funnels through here, so there is exactly one place where a
 * fraction becomes a minor unit. `03-data-model.md` § 5.3: round once, at the
 * line, never an intermediate percentage and never a rate.
 */

export const ROUNDING_MODES = [
  "half_up",
  "half_even",
  "half_down",
  "up",
  "down",
  "ceiling",
  "floor",
] as const;

export type RoundingMode = (typeof ROUNDING_MODES)[number];

export const DEFAULT_ROUNDING: RoundingMode = "half_up";

/**
 * Rounds `numerator / denominator` to an integer. Sign is carried by the
 * numerator, so "up" and "down" mean away from and toward zero, matching the
 * decimal-arithmetic vocabulary rather than the sign-blind one.
 */
export function divideRounded(
  numerator: bigint,
  denominator: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  if (denominator === 0n) throw new RangeError("divideRounded: denominator is zero");
  if (denominator < 0n) {
    numerator = -numerator;
    denominator = -denominator;
  }

  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const quotient = abs / denominator;
  const remainder = abs % denominator;

  if (remainder === 0n) return negative ? -quotient : quotient;

  const twice = remainder * 2n;
  let magnitude: bigint;

  switch (mode) {
    case "half_up":
      magnitude = twice >= denominator ? quotient + 1n : quotient;
      break;
    case "half_down":
      magnitude = twice > denominator ? quotient + 1n : quotient;
      break;
    case "half_even":
      if (twice > denominator) magnitude = quotient + 1n;
      else if (twice < denominator) magnitude = quotient;
      else magnitude = quotient % 2n === 0n ? quotient : quotient + 1n;
      break;
    case "up":
      magnitude = quotient + 1n;
      break;
    case "down":
      magnitude = quotient;
      break;
    case "ceiling":
      magnitude = negative ? quotient : quotient + 1n;
      break;
    case "floor":
      magnitude = negative ? quotient + 1n : quotient;
      break;
  }

  return negative ? -magnitude : magnitude;
}

/** 10 ** exponent as a bigint, for minor-unit and scale conversions. */
export function pow10(exponent: number): bigint {
  if (!Number.isInteger(exponent) || exponent < 0) {
    throw new RangeError(`pow10: exponent must be a non-negative integer, got ${exponent}`);
  }
  return 10n ** BigInt(exponent);
}
